import { Component, ElementRef, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import {
  AuthService, HomeService, IfcService, IProductNameConfiguration, ProductNameService,
  StateService, Utils
} from 'src/app/services';
import { Router } from '@angular/router';
import { CompanyType } from 'identity-webapi-client';
import {
  IfcFile, ListPagedIfcErrorsRequest,
  ListPagedIfcErrorsResponse, IfcParseError,
  SituViewWebApiClient,
  ListIfcFilesByHomeIdRequest,
  ListIfcFilesByHomeIdResponse,
  HomeIfcFile,
  ListHomesByCompanyRequest,
  Home
} from 'situview-webapi-client';
import {
  GetRenderReportRequest, GetRenderReportResponse, RenderReportType
} from 'siturenderservice-webapi-client';
import {
  GetMostPopularRenderedHomesByBuilderRequest, GetMostPopularRenderedHomesByBuilderResponse
} from 'situanalytics-webapi-client';
import { FormControl } from '@angular/forms';
import { animate, keyframes, transition, trigger } from '@angular/animations';
import * as kf from '../../../core/keyframes';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-home-list',
  templateUrl: './home-list.component.html',
  styleUrls: ['./home-list.component.less'],
  animations: [
    trigger('cardAnimator', [
      transition('* => headShake', animate(1000, keyframes(kf.headShake)))
    ])
  ]
})
export class HomeListComponent extends SituBaseComponent {
  @ViewChild('addHomeButton') addHomeButton: ElementRef<HTMLButtonElement> | undefined;

  search: string = '';
  searchFilter: Subject<string> = new Subject<string>();

  selectedProduct: IProductNameConfiguration | undefined;
  products: IProductNameConfiguration[] | undefined;

  ifcFilesLoading: boolean = false;
  homeIfcFiles: IfcFile[] | undefined;
  homeIfcFileErrors: IfcParseError[] | undefined;
  isNotErrorFeed: boolean = true;

  viewsChartLoading: boolean = false;
  public viewsChartLabels: string[] = [];
  public viewsChartData: any[] = [];
  public viewsChartColors: Array<string> = [];

  renderChartLoading: boolean = false;
  public renderAverage: number | undefined;
  public renderChartData: Array<any> = [];
  public renderChartLabels: Array<string> = [];
  public renderChartColors: Array<string> = [];

  animateAddHome: string = '';

  constructor(
    authService: AuthService,
    stateService: StateService,
    private router: Router,
    private productNameService: ProductNameService,
    private homeService: HomeService,
    private ifcService: IfcService,
    private situViewClient: SituViewWebApiClient,
    private snackBar: MatSnackBar) {
      super(authService, stateService);
      this.products = this.productNameService.productNameConfigurations;
      this.selectedProduct = this.productNameService.productNameConfiguration;
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.selectedCompany?.type === CompanyType.Manufacturer) {
      this.router.navigateByUrl('/products/catalog');
    }
  }

  protected onSelectedCompanyChange(): void {
    if (this.selectedCompany?.id) {
      this.loadRenderTimesChart();
      this.loadMostPopularHomes();
      this.loadHomes();
      this.loadIfcReportData();
      this.loadIfcReportErrorData();
    }

  }

  navigateToIfc(home: string, ifcFile?: string, version?: string) {
    if (ifcFile && version) {
      this.router.navigate(['/homes', home], { queryParams: { ifcFile: ifcFile, v: version } });
    } else {
      this.router.navigate(['/homes', home]);
    }
  }

  startAnimation(state: string) {
    if (!this.animateAddHome) {
      this.animateAddHome = state;
    }
  }

  resetAddHomeAnimationState() {
    this.animateAddHome = '';
  }

  onClickHome(home: IHome) {
    this.router.navigate(['/homes', home.home]);
    return;
  };

  onClickAddHome() {
    this.router.navigate([`/homes/upload`]);
    return;
  };

  handleChartClick(home: string) {
    //console.log(home);
  }

  productChange(product: any) {
    this.renderAverage = undefined;
    this.selectedProduct = product.value;
    this.productNameService.productNameConfiguration = product.value;
    this.loadRenderTimesChart();
  };

  toggleExpandedHomesList() {
    this.expandedHomesList = !this.expandedHomesList;
  }

  labelFormatter(tick: string) {
    return tick.split("_")[0];
  }

  doFilter = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredHomeConfigs = this.homeConfigs?.filter(e => { return e.name!.toLocaleLowerCase().includes(searchTerm) });
    } else {
      this.filteredHomeConfigs = this.homeConfigs;
    }
  }

  getEstimatedRenderTime() {
    if (this.selectedProduct && this.renderAverage) {
      return `${this.renderAverage}s`;
    }
    return "Not Available";
  };

  toggleErrorIfcFeed() {
    this.homeIfcFiles = [];
    this.homeIfcFileErrors = [];
    this.isNotErrorFeed = !this.isNotErrorFeed;
    if (this.isNotErrorFeed) {
      this.loadIfcReportData();
    } else {
      this.loadIfcReportErrorData();
    }
  };

  capitalizeFirstLetter(text: string) {
    return Utils.capitalizeFirstLetter(text);
  };

  cachedHomesLoading: boolean = false;
  homeConfigs: Home[] | undefined;
  filteredHomeConfigs: Home[] | undefined;
  searchField = new FormControl();
  expandedHomesList: boolean = false;
  loadHomes() {
    if (!this.selectedCompany?.code) {
      return Promise.resolve();
    }
    this.cachedHomesLoading = true;
    
    let request = new ListHomesByCompanyRequest();
    request.companyId = this.selectedCompany.id;
    request.pageSize = 1000;
    request.isHidden = false;

    return this.homeService.ListHomesByCompany(request)
      .then((homes: Home[]) => {
        this.homeConfigs = homes;
        this.filteredHomeConfigs = [...this.homeConfigs];
        this.cachedHomesLoading = false;
        if (this.homeConfigs?.length === 0) {
          this.startAnimation("headShake");
        }
      })
      .catch((err: any) => {
        this.showMessage("Failed to load homes.");
      })
      .finally(() => {
        this.cachedHomesLoading = false;
      });
  };

  private loadMostPopularHomes() {
    this.viewsChartLoading = true;
    this.viewsChartColors = [];
    this.viewsChartColors.push("rgba(56, 163, 165, 1)");

    let mostPopularHomesRequest = new GetMostPopularRenderedHomesByBuilderRequest();
    mostPopularHomesRequest.ownerId = this.selectedCompany?.id;
    mostPopularHomesRequest.pageSize = 5;
    mostPopularHomesRequest.daysPreviousToSearch = 1;
    this.homeService.GetMostPopularHomesByBuilder(mostPopularHomesRequest)
      .then((response: GetMostPopularRenderedHomesByBuilderResponse) => {
        if (response.success) {
          let dataSet: (number | undefined)[] = [];
          this.viewsChartLabels = [];
          response.logItems?.forEach(logItem => {
            this.viewsChartLabels.push(this.capitalizeFirstLetter(logItem?.homeName ?? ""));
            dataSet.push(logItem.numberOfViews);
          });
          this.viewsChartData = [{ data: dataSet, label: "Home views" }];
        }
        this.viewsChartLoading = false;
      })
      .catch((err: any) => {
        this.viewsChartLoading = false;
      });
  };

  private loadRenderTimesChart() {
    if (!this.selectedCompany?.id) {
      return;
    }

    this.renderChartLoading = true;
    this.renderChartColors = [];
    this.renderChartLabels = [];
    this.renderChartData = [];
    this.products?.forEach(color => {
      if (color.productName === this.selectedProduct?.productName) {
        this.renderChartColors.push(color.productColor);
      }
    });

    let renderReportRequest = new GetRenderReportRequest();
    renderReportRequest.companyId = this.selectedCompany.id;
    renderReportRequest.productName = this.selectedProduct?.productName;
    renderReportRequest.type = RenderReportType.Last5Renders;
    renderReportRequest.timePeriod = 10;
    return this.homeService.GetRenderChartData(renderReportRequest)
      .then((renderReportResponse: any) => {
        let response = renderReportResponse as GetRenderReportResponse;
        if (response.success) {
          let dataSet = [];
          for (let key in response.data) {
            let value = response.data[key];
            let homeConfig = Utils.getHomeConfigurationFromPath(key);
            this.renderChartLabels.push(this.capitalizeFirstLetter(homeConfig?.home ?? ""));
            dataSet.push(value);
          }
          this.renderAverage = Math.round(dataSet.reduce((a, b) => a + b, 0) / dataSet.length);
          this.renderChartData = [{ data: dataSet, label: response.label }];
        }
        this.renderChartLoading = false;
      })
      .catch(err => {
        this.renderChartLoading = false;
      });
  };

  private loadIfcReportData(loadEnvironments: boolean = true) {
    if (!this.selectedCompany?.id) {
      return;
    }

    this.ifcFilesLoading = true;

    let request = new ListIfcFilesByHomeIdRequest();
    request.companyId = this.selectedCompany.id;
    request.orderLatest = true;
    request.justTen = true;

    this.situViewClient.listIfcFilesByHomeId(request)
      .then((response: ListIfcFilesByHomeIdResponse) => {
        if (response.success) {
          this.homeIfcFiles = response.ifcFiles?.map((x: HomeIfcFile) => {
            let homeIfcFile = new IfcFile();
            //homeIfcFile.home = x.home;
            homeIfcFile.home = x.callerReferenceKey?.split('\\')[2];
            homeIfcFile.name = x.name;
            homeIfcFile.dateAdded = x.dateUpdated ?? new Date();
            homeIfcFile.version = x.version;
            return homeIfcFile;
          }) ?? [];
          this.ifcFilesLoading = false;
        }
      })
      .catch(err => {
        this.ifcFilesLoading = false;
      });
  };

  private loadIfcReportErrorData() {
    if (!this.selectedCompany?.code) {
      return;
    }

    this.ifcFilesLoading = true;

    var request = new ListPagedIfcErrorsRequest();
    request.builder = this.selectedCompany.code;
    request.pageNumber = 1;
    request.pageSize = 10;

    this.ifcService.GetIfcReportErrorData(request)
      .then(response => {
        let listPagedHomeIfcFilesResponse = response as ListPagedIfcErrorsResponse;
        this.homeIfcFileErrors = listPagedHomeIfcFilesResponse.items;
        this.ifcFilesLoading = false;
      })
      .catch(err => {
        this.ifcFilesLoading = false;
      });
  };

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

interface IHome {
  home: string;
}

export interface IfcData {
  homeId: string;
  ifc: string;
  callerReferenceKey: string;
}